/**
 * wc-analytics.js — Wolden Customize analytics layer
 *
 * Extracted verbatim from assets/wolden-customize.js lines 550-705.
 * Zero behavior change — pure relocation.
 *
 * Public surface:
 *   export function log(...args)              — verbose console log (always-on; no debug gate)
 *   export function trace(event, data)        — structured trace event (ring buffer + log)
 *   export function track(event, props = {})  — fire event to GA4, PostHog, Klaviyo
 *   export function captureNetworkEvent(...)  — network diagnostic helper (calls track)
 *
 * Cross-module bridge (Sub-project B temporary):
 *   trace() writes to window.WC._debugTrace — the monolith initialises this array;
 *   Sub-project C will replace it with an import from wc-state.js.
 *
 * Extracted from monolith: lines 550–705
 * Helpers NOT extracted (belong to other modules):
 *   isAcceptedFile, showZoneError, clearZoneError, setState (→ wc-upload.js)
 *   tempGenerationId (→ wc-state.js)
 */

// ─── Helpers ──────────────────────────────────────────────────
// Lifted verbatim from monolith lines 550-555.
// Note: always-on (no WC_DEBUG gate) — matches original behaviour.
export function log() {
  if (typeof console !== 'undefined' && console.log) {
    var args = ['[wolden-customize]'].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, args);
  }
}

// ─── Structured trace (ring buffer) ──────────────────────────
// 2026-05-13 — Diagnostic trace for ATC-stuck investigation. Captures key
// state transitions in a 100-entry ring buffer at WC._debugTrace. After a
// failing session, run in DevTools console:
//   copy(JSON.stringify(window.WoldenCustomize._debugTrace, null, 2))
// and paste the result back for analysis.
//
// Sub-project B bridge: writes to window.WC._debugTrace which is
// initialised by the monolith. Sub-project C will replace with wc-state import.
// Lifted verbatim from monolith lines 563-568.
export function trace(event, data) {
  // Bridge: initialise the ring buffer if absent (safe when module loads before monolith).
  if (window.WC && !window.WC._debugTrace) window.WC._debugTrace = [];
  if (window.WC && window.WC._debugTrace) {
    var entry = { t: new Date().toISOString(), event: event, data: data || {} };
    window.WC._debugTrace.push(entry);
    if (window.WC._debugTrace.length > 100) window.WC._debugTrace.shift();
  }
  log('[trace]', event, data || '');
}

// ─── Phase 9 — Analytics tracking layer ───────────────────────
// Forwards Wolden funnel events to GA4 (via window.dataLayer / gtag),
// Klaviyo client (window._learnq), and dispatches a CustomEvent for any
// other analytics integrations (LittleData, GTM, etc.) to subscribe to.
// Lifted verbatim from monolith lines 613-653.
export function track(eventName, properties) {
  var props = properties || {};
  log('track →', eventName, props);

  // GA4 / GTM dataLayer push
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'wolden_' + eventName, ...props });
  } catch (e) { /* ignore */ }

  // gtag direct (in case dataLayer-only didn't pick up)
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'wolden_' + eventName, props);
    }
  } catch (e) { /* ignore */ }

  // Klaviyo client tracker
  try {
    if (window._learnq) {
      window._learnq.push(['track', 'Wolden ' + eventName, props]);
    }
  } catch (e) { /* ignore */ }

  // CustomEvent for any other listener
  try {
    document.dispatchEvent(new CustomEvent('wolden:track', {
      bubbles: true, detail: { eventName: eventName, properties: props },
    }));
  } catch (e) { /* ignore */ }

  // 2026-05-15 — PostHog capture. Snippet loaded conditionally in
  // theme.liquid; window.posthog stays a stub if the project key is
  // unset, so the call is a no-op there. Wraps a try/catch since the
  // posthog snippet may not have hydrated by first track() call.
  try {
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture('wolden_' + eventName, props);
    }
  } catch (e) { /* ignore */ }
}

// ─── 2026-05-15 — Network diagnostic logging ───────────────────
// Captures URL host/path, status, key response headers, timing, and
// body shape for every orchestrator / presigner / S3-PUT fetch.
// Fires track('network_call', ...) so PostHog (+ other analytics)
// get the diagnostic record. Built in response to the 2026-05-14
// "HTTP 429: Too Many Attempts" customer report where AWS CloudWatch
// showed Lambda success while the browser saw a 429-shaped error
// body — without this we can't see which network layer produced it.
//
// PII safety: query strings are stripped (presigned URLs carry
// signature creds); only diagnostic response headers captured (no
// Authorization or Set-Cookie); body fields are `success`/`error`/
// `phase` only — developer strings, never customer content.
//
// Lifted verbatim from monolith lines 669-704.
// Previously exposed as WC.captureNetworkEvent — callers in the monolith
// use that alias; the monolith continues to set WC.captureNetworkEvent
// while coexisting with this module.
export function captureNetworkEvent(label, url, resp, body, error, startedAt, extra) {
  try {
    var u = null;
    try { u = new URL(url); } catch (_) { /* relative or invalid URL */ }
    var headers = {};
    if (resp && resp.headers && typeof resp.headers.get === 'function') {
      var keys = [
        'cf-mitigated', 'cf-ray', 'server', 'content-type', 'retry-after',
        'x-amzn-errortype', 'x-amzn-trace-id', 'x-amzn-requestid',
        'x-amz-request-id', 'x-amz-id-2',
      ];
      for (var i = 0; i < keys.length; i++) {
        var v = resp.headers.get(keys[i]);
        if (v != null) headers[keys[i].replace(/-/g, '_')] = String(v).slice(0, 200);
      }
    }
    var props = {
      label: label,
      url_host: u ? u.host : null,
      url_path: u ? u.pathname : null,
      status: resp ? resp.status : null,
      ok: resp ? !!resp.ok : null,
      duration_ms: startedAt ? (Date.now() - startedAt) : null,
      body_success: (body && typeof body.success === 'boolean') ? body.success : null,
      body_error: (body && body.error) ? String(body.error).slice(0, 200) : null,
      body_phase: (body && body.phase) ? String(body.phase).slice(0, 80) : null,
      error_name: error ? error.name : null,
      error_message: error ? String(error.message || '').slice(0, 200) : null,
    };
    Object.assign(props, headers);
    if (extra) Object.assign(props, extra);
    track('network_call', props);
  } catch (e) {
    log('wcCaptureNetworkEvent failed (non-fatal)', e && e.message);
  }
}
